'use strict'

/**
 * Name: Day14 - Particle
 * ===========================================
 */

import * as PIXI from 'pixi.js'
import { TweenMax, Linear } from 'gsap/TweenMax'
import 'gsap/PixiPlugin.js'
import { rand } from '@/helpers/helpers'

const COLOR = '0xEEEEEE'

/**
 * Particle Class
 *
 * @constructor
 */
export class Particle {
  constructor(el, options = {}) {
    this.parentEl = el
    this.points = []
    this.app = false
    this.ratio = false
    this.container = false
    this.containerChild = false
    this.sprites = []

    this.isScaleUp = true
    this.scale = {
      x: 1.3,
      y: 1.3
    }
    this.SCALE_CHANGE_NUMBER = 0.0015 // アニメーション1回単位で変化させるスケール
    this.SCALE_UP_LIMIT = 2 // 最大のスケール
    this.SCALE_DOWN_LIMIT = 1.3 // 最少のスケール

    window.addEventListener('debounceResize', this.resize.bind(this))

    this.init()
  }

  init() {
    const { width, height } = this.parentEl.getBoundingClientRect()
    this.app = new PIXI.Application(width, height, { transparent: true, resolution: 2, autoResize: true, antialias: true })
    this.ratio = width / height
    this.parentEl.appendChild(this.app.view)
    this.containerChild = new PIXI.ParticleContainer(500, {
      scale: true,
      position: true,
      rotation: true,
      uvs: false,
      alpha: true
    })
    this.app.stage.addChild(this.containerChild)

    this.w = this.app.view.width / 2
    this.h = this.app.view.height / 2

    this.count = Math.min(this.w, this.h) / 5
    this.deltaX = 0.75
    this.delta = 0.15

    const graphics = new PIXI.Graphics()
    graphics.beginFill(COLOR)
    graphics.drawCircle(50, 50, 50)
    graphics.endFill()
    this.texture = this.app.renderer.generateTexture(graphics)

    for (let i = 0; i < this.count; i++) {
      this.initPoint()
    }

    for (let i = 0; i < this.points.length; i++) {
      const s = new PIXI.Sprite(this.texture)
      s.width = 1 * this.points[i].size
      s.height = 1 * this.points[i].size
      s.position.x = this.points[i].x
      s.position.y = this.points[i].y
      s.anchor.x = 0.5
      s.anchor.y = 0.5
      s.alpha = this.points[i].alpha
      this.sprites.push(s)
      this.containerChild.addChild(s)
    }

    this.containerChild.scale.set(1.3)
    this.containerChild.x = this.app.screen.width / 2
    this.containerChild.y = this.app.screen.height / 2
    this.containerChild.pivot.x = this.app.screen.width / 2
    this.containerChild.pivot.y = this.app.screen.height / 2

    // Listen for animate update
    TweenMax.to(this.containerChild, 40, {
      repeat: -1,
      ease: Linear.easeNone,
      pixi: {
        rotation: -360
      }
    })
    TweenMax.to(this.containerChild, 13, {
      repeat: -1,
      yoyo: true,
      ease: Linear.easeNone,
      pixi: {
        scaleX: 1.6,
        scaleY: 1.6
      }
    })

    // Listen for animate update
    this.app.ticker.add(delta => {
      this.updatePosition()
    }, PIXI.UPDATE_PRIORITY.LOW)
  }

  initPoint() {
    const x = this.w * Math.random()
    const y = this.h * Math.random()
    const dx = -this.deltaX / 2 + this.deltaX * Math.random()
    const dy = -this.delta / 2 + this.delta * Math.random()
    const size = rand(5, 8)
    const lineWidth = (size > 4) ? rand(3, 4) : rand(2, 4)

    this.points.push({
      x: x,
      y: y,
      dx: dx,
      dy: dy,
      size: size,
      lineWidth: lineWidth,
      ALPHA_CHANGE_NUMBER: rand(1, 10) * 0.001,
      ALPHA_UP_LIMIT: 1,
      ALPHA_DOWN_LIMIT: 0,
      alpha: rand(1, 10) * 0.1
    })
  }

  destroy() {
    this.app.destroy(true)
    this.app = null
  }

  drawPoint(point, i) {
    this.sprites[i].position.x = point.x
    this.sprites[i].position.y = point.y
    this.sprites[i].alpha = this.points[i].alpha
  }

  updatePosition() {
    for (let i = 0; i < this.points.length; i++) {
      let xDx = this.points[i].x + this.points[i].dx
      let yDy = this.points[i].y + this.points[i].dy
      if (xDx - this.points[i].size < 0 || xDx + this.points[i].size > this.w) {
        this.points[i].dx = -this.points[i].dx
      }
      if (yDy - this.points[i].size < 0 || yDy + this.points[i].size > this.h) {
        this.points[i].dy = -this.points[i].dy
      }
      this.points[i].x += this.points[i].dx
      this.points[i].y += this.points[i].dy

      // for Alpha Update
      if (this.points[i].isScaleUp) {
        this.points[i].alpha += this.points[i].ALPHA_CHANGE_NUMBER
        if (this.points[i].alpha > this.points[i].ALPHA_UP_LIMIT) {
          this.points[i].alpha = this.points[i].ALPHA_UP_LIMIT - this.points[i].ALPHA_CHANGE_NUMBER
          this.points[i].isScaleUp = false
        }
      } else {
        this.points[i].alpha -= this.points[i].ALPHA_CHANGE_NUMBER
        if (this.points[i].alpha < this.points[i].ALPHA_DOWN_LIMIT) {
          this.points[i].x = this.w * Math.random()
          this.points[i].y = this.h * Math.random()
          this.points[i].alpha = this.points[i].ALPHA_DOWN_LIMIT + this.points[i].ALPHA_CHANGE_NUMBER
          this.points[i].isScaleUp = true
        }
      }

      this.drawPoint(this.points[i], i)
    }
  }

  resize() {
    const { width, height } = this.parentEl.getBoundingClientRect()
    let w
    let h
    if (width / height >= this.ratio) {
      w = height * this.ratio
      h = height
    } else {
      w = width
      h = width / this.ratio
    }
    this.app.view.style.width = `${w}px`
    this.app.view.style.height = `${h}px`
  }
}
